import React from 'react';

export type PresetDays = 7 | 30 | 90;

/** Every analytics page opens on this window. */
export const DEFAULT_PRESET: PresetDays = 30;

const DAY_MS = 86_400_000;

function shift(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

export function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The inclusive `[from, to]` dates a trailing-N-days preset stands for. */
export function presetRange(days: PresetDays): [string, string] {
  const to = todayInput();
  return [shift(to, 1 - days), to];
}

export function defaultRange(): [string, string] {
  return presetRange(DEFAULT_PRESET);
}

/**
 * Which preset a range is: N for trailing N days, 'all' for no bounds, null for
 * a custom range. Endpoints that take `days` rather than `from`/`to` read this.
 */
export function rangePreset(from: string | null, to: string | null): PresetDays | 'all' | null {
  if (from === null && to === null) return 'all';
  return (
    ([7, 30, 90] as PresetDays[]).find((d) => {
      const [pf, pt] = presetRange(d);
      return pf === from && pt === to;
    }) ?? null
  );
}

/**
 * The window of the same length immediately before `[from, to]`, for
 * period-over-period comparisons. Null when there is no such window (all time,
 * or only one bound set).
 */
export function priorRange(from: string | null, to: string | null): [string, string] | null {
  if (!from || !to || to < from) return null;
  const days = Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;
  return [shift(from, -days), shift(from, -1)];
}

const PRESETS: { label: string; range: () => [string | null, string | null] }[] = [
  { label: '7d', range: () => presetRange(7) },
  { label: '30d', range: () => presetRange(30) },
  { label: '90d', range: () => presetRange(90) },
  { label: 'All time', range: () => [null, null] },
];

const dateInputClass =
  'px-2.5 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-gray-200 text-xs focus:outline-none focus:border-violet-500';

export interface DateRangeControlProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  label?: string;
  /** False where the endpoint only understands the presets: hides the custom date inputs. */
  custom?: boolean;
}

/**
 * The date window every analytics page shares: 7d / 30d / 90d / All time, plus
 * a custom range. Presets compute plain `YYYY-MM-DD` strings client-side;
 * `null` on both means all time. `to` is inclusive of that day.
 */
export function DateRangeControl({
  from,
  to,
  onChange,
  label,
  custom = true,
}: DateRangeControlProps): React.ReactElement {
  const active = rangePreset(from, to);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {label && <span className="text-xs text-gray-500">{label}</span>}
      <div className="flex gap-1">
        {PRESETS.map((p) => {
          const [pf, pt] = p.range();
          const isActive = rangePreset(pf, pt) === active && active !== null;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => onChange(pf, pt)}
              aria-pressed={isActive}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md border ${
                isActive
                  ? 'bg-violet-950/50 border-violet-700 text-violet-300'
                  : 'bg-gray-900 border-gray-800 text-gray-500 hover:text-gray-300'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {custom && (
        <>
          <input
            type="date"
            value={from ?? ''}
            max={to ?? undefined}
            onChange={(e) => onChange(e.target.value || null, to)}
            className={dateInputClass}
            aria-label="From date"
          />
          <span className="text-xs text-gray-600">to</span>
          <input
            type="date"
            value={to ?? ''}
            min={from ?? undefined}
            onChange={(e) => onChange(from, e.target.value || null)}
            className={dateInputClass}
            aria-label="To date"
          />
        </>
      )}
    </div>
  );
}

export default DateRangeControl;
